import React, { useMemo, useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import { Box, Alert, Button, Typography, CircularProgress, LinearProgress } from '@mui/material'
import PlatinumTable, { type MarkedCell } from '../platinum-table-new/PlatinumTable'
import PlatinumTableDebug from '../platinum-table-new/PlatinumTableDebug'
import PlatinumGraphs from '../platinum-graphs/PlatinumGraphs'
import GraphViewControls from '../platinum-graphs/GraphViewControls'
import TableControls from '../platinum-table-new/TableControls'
import MetadataModal from '../platinum-table-new/MetadataModal'
import { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { TableViewSettingsData, TableVisibilitySettings, ChartDesign, ChartSortOrder, TableDesign } from '../../types/gotypes'
import { GraphConfig, ChartType, DataType } from '../platinum-graphs/types'
import { validators } from '../platinum-graphs/validators'
import { convertPlatinumToTable, extractRowLabels, extractColumnLabels, extractRowTypes, extractColumnTypes } from '../platinum-graphs/utils/convertPlatinumData'
import { exportChartToPptx } from '../../utils/pptxExport'
import { getSelectedColumnLabel } from '../platinum-graphs/utils/selectionHelpers'
import useRouter from '../../hooks/useRouter'
import useSnackbar from '../../hooks/useSnackbar'
import useAccount from '../../hooks/useAccount'
import useIsUserTable from '../../hooks/useIsUserTable'
import useTableData from '../../hooks/useTableData'
import { useDashboardGrantId } from '../../contexts/DashboardGrantContext'
import useTableConfig from '../../hooks/useTableConfig'
import { convertViewSettingsToURL, convertVisibilitySettingsToURL, getMergedURLTableViewSettings, getDefaultTableViewSettings, getURLBasedVisibilitySettings, getAllTableSettingParamNames, getAllVisibilityParamNames, resolveChartDataType } from '../../utils/tables'
import { MergedConfigResult, ConfigType } from '../../types/config'
import { openSavedTableXlsx } from '../../utils/tableXlsxDownload'
import { tableRef } from '../../utils/outputStamp'
import { configApi } from '../../services/configApi'

export type SideLabelsMode = 'full' | 'hide-groups' | 'hide-all'

/** What the chart view offers and shows, for a page tool (#1534). */
export interface WrapperChartState {
  allowedChartTypes: ChartType[]
  rows: string[]
  columns: string[]
  rowTypes: string[]
  columnTypes: string[]
  /** The view settings the chart toolbar shows (data type resolved). */
  settings: TableViewSettingsData | undefined
}

export interface PlatinumTableWrapperHandle {
  saveViewSettings: () => Promise<void>
  discardViewSettings: () => void
  hasUnsavedChanges: boolean
  currentVisibility: TableVisibilitySettings | undefined
  // The toolbar's own controls, so a page tool drives the same code (#1534).
  viewMode: 'table' | 'graph'
  setViewMode: (mode: 'table' | 'graph') => void
  fullscreen: boolean
  setFullscreen: (on: boolean) => void
  sideLabelsMode: SideLabelsMode
  setSideLabelsMode: (mode: SideLabelsMode) => void
  openMetadata: () => void
  exportChartPptx: () => Promise<void>
  chartState: () => WrapperChartState
  /** Change chart settings as the chart toolbar does, and show the chart. */
  setChartSettings: (patch: Partial<TableViewSettingsData>) => void
}

export interface PlatinumTableWrapperProps {
  // Option 1: Provide customer/job/tablePath for auto-loading
  /** Customer name - for auto-loading table data and config */
  customer?: string
  /** Job name - for auto-loading table data and config */
  job?: string
  /** Table path - for auto-loading table data and config */
  tablePath?: string
  /** Filter parameter for table data */
  filter?: string

  // Option 2: Provide data directly (for modals, previews)
  /** The platinum data to display directly (skips auto-loading) */
  data?: PlatinumData

  // Optional overrides (for preview mode, special cases)
  /** Properties that should be disabled (e.g., significance when viewing preview) */
  hiddenProperties?: Partial<Record<'frequency' | 'column_percent' | 'row_percent' | 'significance', boolean>>
  /** Hide control bars completely */
  hideControls?: boolean
  /** Optional prefix for URL params to avoid conflicts with multiple wrappers */
  urlParamPrefix?: string

  // Callbacks
  /** Called when table data is loaded (for parent components that need the data) */
  onDataLoaded?: (data: PlatinumData) => void
  /** Called when an error occurs during loading */
  onError?: (error: Error) => void
  /**
   * When provided, visibility toggle changes call this instead of updating URL params.
   * Used in spec preview mode so toggles update the CarbonSpec and re-run.
   */
  onVisibilityOverride?: (settings: Partial<Record<'frequency' | 'column_percent' | 'row_percent' | 'significance', boolean>>) => void

  /** Quick filter callbacks - opens filter modal and clears filter */
  onQuickFilter?: () => void
  onClearFilter?: () => void
  /** Settings drawer callback */
  onSettings?: () => void
  /**
   * Called with a series name when the viewer clicks it in the graph legend.
   * Passed straight to PlatinumGraphs — see its one-way caveat (#968).
   */
  onLegendToggle?: (seriesName: string) => void

  // Deprecated props (kept for backward compatibility during migration)
  /** @deprecated Use tablePath instead */
  path?: string
  /** @deprecated Now loaded internally */
  currentViewSettings?: TableViewSettingsData
  /** @deprecated Now loaded internally */
  savedViewSettings?: TableViewSettingsData
  /** @deprecated Now loaded internally */
  tableConfig?: MergedConfigResult
  /** @deprecated Now handled internally */
  saveSettings?: (settings: TableViewSettingsData) => Promise<any>
  /** Optional brand-to-color map for consistent chart colors across dashboard widgets */
  colorMap?: Record<string, string>
  /** Called when unsaved changes state changes (for parent to track) */
  onUnsavedChangesChange?: (hasChanges: boolean) => void
  /** Chart design overrides (#968 Part C) — axis labels/titles, gridlines. Passed straight through to PlatinumGraphs. */
  design?: ChartDesign
  /** Bar order for the bar-family charts (#1808) — a dashboard widget's sort_order. Reaches the chart and its PPTX export. Absent keeps each chart type's old order. */
  sortOrder?: ChartSortOrder
  /** Per-column pixel widths keyed by data column index (#969 Part C). */
  columnWidths?: Record<number, number>
  /** Unpins the stub and column headers (#969 Part B freeze toggle, default on). */
  disableSticky?: boolean
  /** Table design overrides (#969 Part C) — header/cell styling, conditional formatting, significance. Passed straight through to PlatinumTable (table view mode only). */
  tableDesign?: TableDesign
  /**
   * Graph mode: make the chart FIT the height the parent gives it instead of
   * demanding PlatinumGraphs' standalone 400px minimum (#1177). Height-boxed
   * contexts (dashboard widgets) must pass true, or the chart's bottom —
   * x-axis labels included — is clipped whenever the slot is under 400px.
   */
  respectParentHeight?: boolean
  /**
   * A cited cell to highlight in table view (#1296), addressed by row and
   * column LABEL rather than position so it survives a table reflow. Passed
   * straight through to PlatinumTable; a cell the table does not have is
   * simply not highlighted.
   */
  highlightCell?: { row: string; column: string }
  /** Cells to mark in amber by data index (#1647), passed straight to PlatinumTable. */
  markedCells?: MarkedCell[]
  /**
   * Download-to-Excel handler for the toolbar button. In auto-load mode with
   * none given, the wrapper downloads the saved table itself.
   */
  onDownloadXlsx?: () => void
}

/**
 * PlatinumTableWrapper Component
 *
 * A comprehensive wrapper around PlatinumTable and PlatinumGraphs that manages:
 * - URL-based state for all display options
 * - View mode switching (table/graph)
 * - Visible properties (frequency, column %, row %, significance)
 * - Vertical and horizontal scaling coefficients
 * - Fullscreen mode
 * - Graph configuration (chart type, data type, row/column selections)
 * - Auto-loading of table data, config, and view settings
 *
 * This component provides a complete table viewing experience with minimal props.
 *
 * Usage:
 * 1. Auto-load mode: Provide customer/job/tablePath
 * 2. Manual mode: Provide data directly (for modals, previews)
 */
export const PlatinumTableWrapper = forwardRef<PlatinumTableWrapperHandle, PlatinumTableWrapperProps>(({
  customer,
  job,
  tablePath,
  filter,
  data: providedData,
  hiddenProperties,
  hideControls = false,
  urlParamPrefix = '',
  onDataLoaded,
  onError,
  onVisibilityOverride,
  onQuickFilter,
  onClearFilter,
  onSettings,
  onLegendToggle,
  // Deprecated props (backward compatibility)
  path,
  currentViewSettings: providedCurrentViewSettings,
  savedViewSettings: providedSavedViewSettings,
  tableConfig: providedTableConfig,
  saveSettings: providedSaveSettings,
  colorMap,
  onUnsavedChangesChange,
  design,
  sortOrder,
  columnWidths,
  disableSticky,
  highlightCell,
  markedCells,
  tableDesign,
  respectParentHeight = false,
  onDownloadXlsx: providedOnDownloadXlsx,
}, ref) => {
  const router = useRouter()
  const snackbar = useSnackbar()
  const account = useAccount()

  // Check if user is a manager (can bypass cache)
  const isManager = account.user?.role_permissions?.isManager || false

  // Compact side labels: 'full' → 'hide-groups' → 'hide-all' → 'full'
  const [sideLabelsMode, setSideLabelsMode] = useState<SideLabelsMode>('full')

  // localStorage key for cache bypass preference
  const CACHE_PREFERENCE_KEY = 'platinum_table_use_cache'

  // Cache bypass state (managers only) - defaults to true (cache enabled)
  // Initialize from localStorage if available
  const [useCache, setUseCache] = useState(() => {
    if (typeof window === 'undefined') return true
    const stored = localStorage.getItem(CACHE_PREFERENCE_KEY)
    // Default to true (cache enabled) if not set
    return stored === null ? true : stored === 'true'
  })

  // Persist cache preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(CACHE_PREFERENCE_KEY, String(useCache))
  }, [useCache])

  // Support both tablePath and path (backward compat)
  const effectiveTablePath = tablePath || path

  // Determine if we should auto-load data
  const shouldAutoLoadData = !providedData && customer && job && effectiveTablePath

  // Determine if we should load config/settings (even when data is provided, we need settings)
  const shouldLoadConfigAndSettings = Boolean(customer && job && effectiveTablePath)

  // Get URL-based visibility settings for auto-loading
  const urlBasedVisibility = useMemo(() => {
    if (!shouldAutoLoadData) return {}
    return getURLBasedVisibilitySettings(urlParamPrefix)
  }, [shouldAutoLoadData, urlParamPrefix, router.params])

  // Inside a dashboard, the workbook download names it too (the dashboard grant).
  const grantDashboardId = useDashboardGrantId()

  // Auto-load table data and view settings
  const tableData = useTableData({
    customer: shouldLoadConfigAndSettings ? (customer || '') : '',
    job: shouldLoadConfigAndSettings ? (job || '') : '',
    table: shouldLoadConfigAndSettings ? (effectiveTablePath || '') : '',
    filter: shouldAutoLoadData ? (filter || '') : '',
    visibility: urlBasedVisibility as Partial<TableVisibilitySettings>,
    useCache: useCache,
  })

  // Auto-load table config
  const {
    tableConfig: loadedTableConfig,
    loadingTableConfig
  } = useTableConfig(
    shouldLoadConfigAndSettings ? customer : undefined,
    shouldLoadConfigAndSettings ? job : undefined,
    shouldLoadConfigAndSettings ? effectiveTablePath : undefined
  )

  // Determine effective data (provided or loaded)
  const effectiveData = providedData || tableData.data

  // Determine effective table config (provided or loaded)
  const effectiveTableConfig = providedTableConfig || loadedTableConfig

  // Stable baseline ref: captures CBT defaults from the first response only,
  // so that subsequent responses (reflecting user overrides) don't shift the baseline.
  const baselineRef = useRef<TableViewSettingsData | null>(null)

  // Reset baseline when table changes (component doesn't remount between tables)
  useEffect(() => {
    baselineRef.current = null
  }, [effectiveTablePath])

  // Compute view settings (provided or derive from CBT properties in response data)
  const computedSavedViewSettings = useMemo(() => {
    if (providedSavedViewSettings) return providedSavedViewSettings

    // If we already have a baseline, keep using it (don't recompute from new response data)
    if (baselineRef.current) return baselineRef.current

    const defaults = getDefaultTableViewSettings()
    const props = effectiveData?.properties
    if (props?.cells) {
      defaults.visibility = {
        frequency: props.cells.frequencies ?? false,
        column_percent: props.cells.columnPercents ?? true,
        row_percent: props.cells.rowPercents ?? false,
        significance: props.significance?.visible ?? false,
      }
    }

    // If table config has saved view settings, use those as the baseline
    const savedSettings = effectiveTableConfig?.data?.table_view_settings
    if (savedSettings) {
      Object.assign(defaults, savedSettings)
    }

    // Only store baseline once we have actual response data
    if (effectiveData) {
      baselineRef.current = defaults
    }

    return defaults
  }, [providedSavedViewSettings, effectiveData?.properties, effectiveTableConfig?.data?.table_view_settings])

  const computedCurrentViewSettings = useMemo(() => {
    if (providedCurrentViewSettings) return providedCurrentViewSettings
    return getMergedURLTableViewSettings(computedSavedViewSettings, urlParamPrefix)
  }, [providedCurrentViewSettings, computedSavedViewSettings, urlParamPrefix, router.params])

  // Use the computed values
  const currentViewSettings = computedCurrentViewSettings
  const savedViewSettings = computedSavedViewSettings
  const tableConfig = effectiveTableConfig

  // Callback when data is loaded
  useEffect(() => {
    if (effectiveData && onDataLoaded) {
      onDataLoaded(effectiveData)
    }
  }, [effectiveData, onDataLoaded])

  // Callback when error occurs
  useEffect(() => {
    if (tableData.isError && tableData.error && onError) {
      onError(tableData.error as Error)
    }
  }, [tableData.isError, tableData.error, onError])

  // State for metadata modal (used in graph view)
  const [metadataModalOpen, setMetadataModalOpen] = useState(false)
  const [isReloading, setIsReloading] = useState(false)

  // State for tracking unsaved view settings changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Check if user is admin (roles are in session_info)
  const isAdmin = useMemo(() => {
    const roles = account.user?.session_info?.roles || []
    return roles.includes('admin')
  }, [account.user])

  // Check if current table is a user table (case-insensitive)
  const isUserTable = useIsUserTable(effectiveTablePath)

  // Determine if user has permission to save view settings (user table OR admin)
  const canSaveViewSettings = isUserTable || isAdmin

  // Track initial load to prevent auto-save on mount
  const isInitialLoadRef = useRef(true)

  // Track last saved values to prevent unnecessary saves
  const lastSavedValuesRef = useRef<TableViewSettingsData | null>(null)

  // Reset initial load flag and last saved values when table changes
  useEffect(() => {
    isInitialLoadRef.current = true
    lastSavedValuesRef.current = null
  }, [customer, job, effectiveTablePath])

  // Helper to get param name with optional prefix
  const param = (name: string) => urlParamPrefix ? `${urlParamPrefix}_${name}` : name

  // Derive view mode from current settings (default: 'table')
  const viewMode = useMemo(() => {
    const vm = currentViewSettings?.view_mode
    if (vm === 'graph') return 'graph'
    // Backward compat: check legacy URL param
    const params = new URLSearchParams(window.location.search)
    const legacyView = params.get(param('view'))
    return legacyView === 'graph' ? 'graph' : 'table'
  }, [currentViewSettings?.view_mode, router.params, urlParamPrefix])

  // Extract rows, columns, and allowed chart types from data (needed for graph defaults)
  const graphMetadata = useMemo((): {
    allRows: string[];
    allColumns: string[];
    allRowTypes: string[];
    allColumnTypes: string[];
    allowedChartTypes: ChartType[];
  } => {
    if (!effectiveData) {
      return {
        allRows: [],
        allColumns: [],
        allRowTypes: [],
        allColumnTypes: [],
        allowedChartTypes: ['line'] as ChartType[],
      }
    }

    // Extract row and column labels and types directly from PlatinumData
    const allRows = extractRowLabels(effectiveData)
    const allColumns = extractColumnLabels(effectiveData)
    const allRowTypes = extractRowTypes(effectiveData)
    const allColumnTypes = extractColumnTypes(effectiveData)

    // Parse table for validation
    const parsedTable = convertPlatinumToTable(effectiveData, 'freq')
    if (!parsedTable) {
      return {
        allRows,
        allColumns,
        allRowTypes,
        allColumnTypes,
        allowedChartTypes: ['line'] as ChartType[],
      }
    }

    // Determine allowed chart types
    const allowedChartTypes: ChartType[] = []
    const chartTypeKeys = Object.keys(validators) as Array<keyof typeof validators>
    for (const chartType of chartTypeKeys) {
      if (validators[chartType](parsedTable)) {
        allowedChartTypes.push(chartType)
      }
    }

    return {
      allRows,
      allColumns,
      allRowTypes,
      allColumnTypes,
      allowedChartTypes: (allowedChartTypes.length > 0 ? allowedChartTypes : ['line']) as ChartType[],
    }
  }, [effectiveData])

  const fullscreen = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return params.has(param('fullscreen')) ? params.get(param('fullscreen')) === 'true' : false
  }, [router.params, urlParamPrefix])

  // Check if current settings differ from saved settings
  const settingsHaveChanged = useMemo(() => {
    // Deep equality check for settings
    return JSON.stringify(currentViewSettings) !== JSON.stringify(savedViewSettings)
  }, [currentViewSettings, savedViewSettings])
  
  // Update hasUnsavedChanges state when settings change
  useEffect(() => {
    setHasUnsavedChanges(settingsHaveChanged)
    onUnsavedChangesChange?.(settingsHaveChanged)
  }, [settingsHaveChanged])

  // Convert to GraphConfig format for PlatinumGraphs component
  const graphConfig = useMemo<GraphConfig>(() => {
    // Convert selectedColumn from index to name if needed
    const selectedColumn = getSelectedColumnLabel(
      currentViewSettings?.selected_column,
      graphMetadata.allColumns
    )

    // Fault 4 (#1173): TABLE and CHART share one `data_type` field, and the
    // shared default is 'freq' — which the table half never reads. A caller
    // that hands us `currentViewSettings` directly (the dashboard chart
    // widget, spec previews with their own state) has already made its own
    // data_type decision, so it always wins. Otherwise (the tables module's
    // own auto-loaded/URL-driven settings) a URL override or a saved view
    // setting still wins; only a genuinely untouched table defaults its
    // chart to Column %.
    const hasExplicitDataType = Boolean(
      providedCurrentViewSettings ||
      router.params[param('data_type')] ||
      effectiveTableConfig?.data?.table_view_settings?.data_type
    )

    return {
      chartType: currentViewSettings?.chart_type as ChartType,
      dataType: resolveChartDataType({
        currentDataType: currentViewSettings?.data_type || 'freq',
        explicit: hasExplicitDataType,
      }) as DataType,
      selectedRows: currentViewSettings?.selected_rows || [],
      selectedColumns: currentViewSettings?.selected_columns || [],
      selectedColumn: selectedColumn,
      switchAxis: currentViewSettings?.switch_axis || false,
      sortOrder,
    }
  }, [
    currentViewSettings,
    sortOrder,
    graphMetadata.allColumns,
    providedCurrentViewSettings,
    router.params,
    urlParamPrefix,
    effectiveTableConfig?.data?.table_view_settings?.data_type,
  ])

  const handleExportPptx = async () => {
    if (!effectiveData || !graphConfig) return
    const table = convertPlatinumToTable(effectiveData, graphConfig.dataType)
    if (!table) return
    await exportChartToPptx({
      table,
      config: graphConfig,
      allRowLabels: graphMetadata.allRows,
      allRowTypes: graphMetadata.allRowTypes,
      allColumnLabels: graphMetadata.allColumns,
      allColumnTypes: graphMetadata.allColumnTypes,
      colorMap,
      title: tablePath?.split('/').pop()?.replace(/\.[^.]+$/, '') ?? 'Chart',
      includeDataTable: false,
      design,
      stamp: {
        who: account.user?.session_info?.email || account.user?.session_info?.name || '',
        ref: tableRef(job || '', effectiveTablePath || ''),
      },
    })
  }

  // Single consolidated handler for all view settings changes
  // This updates URL params only - saving to DB happens via manual save button
  const handleViewSettingsChange = (newSettings: Partial<TableViewSettingsData>) => {
    const urlParams = convertViewSettingsToURL(newSettings, savedViewSettings, urlParamPrefix)
    const allPossibleParams = getAllTableSettingParamNames(urlParamPrefix)

    // Build final params: current params minus all table settings, plus new divergent params
    const currentParams = { ...router.params }
    allPossibleParams.forEach(param => {
      delete currentParams[param]
    })
    const finalParams = { ...currentParams, ...urlParams }

    // Single atomic navigation with the final params
    router.navigate(router.name, finalParams)
  }

  const handleVisibilitySettingsChange = (newSettings: Partial<TableVisibilitySettings>) => {
    // Merge the change with current visibility settings to preserve other settings
    const mergedSettings = {
      ...currentViewSettings?.visibility,
      ...newSettings
    }

    // Check if this would result in no visibility settings enabled
    const hasAnyEnabled = mergedSettings.frequency ||
                          mergedSettings.column_percent ||
                          mergedSettings.row_percent ||
                          mergedSettings.significance

    if (!hasAnyEnabled) {
      snackbar.warning('At least one visibility setting must be enabled')
      return
    }

    // If override callback is provided (spec preview mode), use it instead of URL params
    if (onVisibilityOverride) {
      onVisibilityOverride(newSettings)
      return
    }

    const urlParams = convertVisibilitySettingsToURL(mergedSettings, savedViewSettings?.visibility, urlParamPrefix)
    const allPossibleParams = getAllVisibilityParamNames(urlParamPrefix)

    // Build final params: current params minus all visibility settings, plus new divergent params
    const currentParams = { ...router.params }
    allPossibleParams.forEach(param => {
      delete currentParams[param]
    })
    const finalParams = { ...currentParams, ...urlParams }

    // Single atomic navigation with the final params
    router.navigate(router.name, finalParams)
  }

  const handleSaveSettings = async () => {
    if (!canSaveViewSettings || !customer || !job || !effectiveTablePath || !currentViewSettings) {
      return
    }

    try {
      // Use provided save function if available (backward compat)
      const saveFn = providedSaveSettings
      if (saveFn) {
        await saveFn(currentViewSettings)
      } else {
        // Save view settings to table config via config API
        const folderPath = effectiveTablePath.substring(0, effectiveTablePath.lastIndexOf('/')) || '/'
        const tableName = effectiveTablePath.split('/').pop() || ''

        // Try to find existing table config
        const configs = await configApi.listConfigs(customer, job, {
          config_type: ConfigType.Table,
          customer,
          job,
          folder_path: folderPath,
          table_name: tableName,
        })

        const configData = { table_view_settings: currentViewSettings }

        if (configs.length > 0) {
          await configApi.updateConfig(customer, job, configs[0].id, {
            ...configs[0],
            data: { ...configs[0].data, ...configData },
          })
        } else {
          await configApi.createConfig(customer, job, {
            config_type: ConfigType.Table,
            customer,
            job,
            folder_path: folderPath,
            table_name: tableName,
            data: configData,
          })
        }
      }

      // Update baseline so future changes are compared against this state
      baselineRef.current = { ...currentViewSettings }
      snackbar.success('Settings saved')
      setHasUnsavedChanges(false)
    } catch (error) {
      snackbar.error(`Failed to save settings: ${(error as Error).message}`)
    }
  }

  const handleDiscardSettings = () => {
    // Reset URL params to match saved settings
    router.removeParams([
      'frequency',
      'column_percent',
      'row_percent',
      'significance',

      'vertical_coefficient',
      'horizontal_coefficient',
      'chart_type',
      'data_type',
      'selected_rows',
      'selected_columns',
      'selected_column',
      'switch_axis',
      'view_mode',
      'view', // legacy param
    ])
  }

  const handleFullscreenToggle = () => {
    router.setParams({ [param('fullscreen')]: (!fullscreen).toString() })
  }

  // Expose save/discard/hasChanges/visibility and the toolbar's controls to
  // the parent. No dependency list: the handle is rebuilt each render, so a
  // page tool never calls a stale closure.
  useImperativeHandle(ref, (): PlatinumTableWrapperHandle => ({
    saveViewSettings: handleSaveSettings,
    discardViewSettings: handleDiscardSettings,
    hasUnsavedChanges,
    currentVisibility: currentViewSettings?.visibility,
    viewMode,
    setViewMode: (mode) => {
      if (mode !== viewMode) handleViewSettingsChange({ ...currentViewSettings, view_mode: mode })
    },
    fullscreen,
    setFullscreen: (on) => {
      if (on !== fullscreen) handleFullscreenToggle()
    },
    sideLabelsMode,
    setSideLabelsMode,
    openMetadata: () => setMetadataModalOpen(true),
    exportChartPptx: () => handleExportPptx(),
    chartState: () => ({
      allowedChartTypes: graphMetadata.allowedChartTypes,
      rows: graphMetadata.allRows,
      columns: graphMetadata.allColumns,
      rowTypes: graphMetadata.allRowTypes,
      columnTypes: graphMetadata.allColumnTypes,
      settings: currentViewSettings ? { ...currentViewSettings, data_type: graphConfig.dataType } : undefined,
    }),
    setChartSettings: (patch) => {
      if (!currentViewSettings) return
      handleViewSettingsChange({ ...currentViewSettings, data_type: graphConfig.dataType, ...patch, view_mode: 'graph' })
    },
  }))

  const handleViewModeToggle = () => {
    const newMode = viewMode === 'table' ? 'graph' : 'table'
    handleViewSettingsChange({ ...currentViewSettings, view_mode: newMode })
  }

  const handleInfoClick = () => {
    setMetadataModalOpen(true)
  }

  const handleCloseMetadataModal = () => {
    setMetadataModalOpen(false)
  }

  // Handle table data reload (deletes cache record and refetches)
  const handleReloadTable = async () => {
    if (!tableData.reloadTable) return

    // Close the modal first
    setMetadataModalOpen(false)

    setIsReloading(true)
    try {
      await tableData.reloadTable()
    } finally {
      setIsReloading(false)
    }
  }

  // Check if the loaded data has any significance data in its cells
  const hasSigData = useMemo(() => {
    const rows = effectiveData?.cells?.rows
    if (!rows) return false
    return rows.some(row => row?.cell?.some(c => c?.sig))
  }, [effectiveData])

  const metadataModal = effectiveData ? (
    <MetadataModal
      open={metadataModalOpen}
      onClose={handleCloseMetadataModal}
      data={effectiveData}
      tableName={effectiveData?.name ?? undefined}
      timing={tableData.tableTiming}
      onReload={handleReloadTable}
      isReloading={isReloading}
    />
  ) : null

  // Track elapsed time for loading indicator
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null)

  useEffect(() => {
    if (!tableData.loading) {
      setElapsedSeconds(0)
      setLoadingStartTime(null)
      return
    }

    // Set start time when loading begins
    if (!loadingStartTime) {
      setLoadingStartTime(Date.now())
    }

    // Update elapsed time every 100ms for smooth progress bar
    const interval = setInterval(() => {
      const startTime = tableData.tableTiming?.started || loadingStartTime
      if (startTime) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000)
        setElapsedSeconds(elapsed)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [tableData.loading, tableData.tableTiming?.started, loadingStartTime])

  // Show loading spinner if we're loading data
  if (!effectiveData) {
    if (shouldAutoLoadData && tableData.loading) {
      // Don't show anything for the first 3 seconds
      if (elapsedSeconds < 3) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, flex: 1 }}>
            <CircularProgress />
          </Box>
        )
      }

      const hasEstimate = tableData.tableTiming?.estimated && tableData.tableTiming.estimated > 0
      const estimatedSeconds = hasEstimate ? Math.ceil((tableData.tableTiming?.estimated || 0) / 1000) : 0
      const progress = hasEstimate && estimatedSeconds > 0
        ? Math.min((elapsedSeconds / estimatedSeconds) * 100, 100)
        : 0

      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          flex: 1,
          gap: 2
        }}>
          <Typography variant="body1" color="text.primary">
            Loading: {elapsedSeconds} seconds elapsed
          </Typography>
          {hasEstimate && (
            <>
              <Typography variant="caption" color="text.secondary">
                This took {estimatedSeconds} seconds last time
              </Typography>
              <Box sx={{ width: '300px' }}>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            </>
          )}
          <CircularProgress />
        </Box>
      )
    }

    // Show error if loading failed
    if (tableData.isError && tableData.error) {
      const errorMessage = tableData.error instanceof Error
        ? tableData.error.message
        : String(tableData.error)

      return (
        <Box sx={{ p: 3, flex: 1 }}>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => tableData.refetch?.()}
                sx={{ textTransform: 'none' }}
              >
                Retry
              </Button>
            }
          >
            <Typography variant="subtitle2" gutterBottom>
              Failed to load table
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      )
    }

    // No data and not loading - return null
    return null
  }

  // Render table view (fullscreen mode)
  if (viewMode === 'table') {
    const downloadXlsx = providedOnDownloadXlsx || (shouldAutoLoadData
      ? () => openSavedTableXlsx({
          customer,
          job,
          tablePath: effectiveTablePath,
          visibility: currentViewSettings!.visibility,
          filter,
          token: (account.user && (account.user as any).token) || '',
          dashboard: grantDashboardId,
        })
      : undefined)

    const controls = currentViewSettings && !hideControls && (
      <TableControls
        viewSettings={currentViewSettings}
        onViewSettingsChange={handleViewSettingsChange}
        onVisibilitySettingsChange={handleVisibilitySettingsChange}
        hiddenProperties={hiddenProperties}
        fullscreen={fullscreen}
        onFullscreenToggle={handleFullscreenToggle}
        onChartView={handleViewModeToggle}
        onInfoClick={handleInfoClick}
        onDownloadXlsx={downloadXlsx}
        showCacheSwitch={isManager}
        useCache={useCache}
        onCacheToggle={setUseCache}
        filter={filter}
        onQuickFilter={onQuickFilter}
        onClearFilter={onClearFilter}
        onSettings={onSettings}
        hasSigData={hasSigData}
        sideLabelsMode={sideLabelsMode}
        onCompactSideLabelsToggle={() => setSideLabelsMode(prev => prev === 'full' ? 'hide-groups' : prev === 'hide-groups' ? 'hide-all' : 'full')}
      />
    )

    const table = tableConfig?.data.table_view_mode === 'debug' ? (
      <PlatinumTableDebug
        data={effectiveData}
        viewSettings={currentViewSettings}
      />
    ) : (
      <PlatinumTable
        data={effectiveData}
        viewSettings={currentViewSettings}
        sideLabelsMode={sideLabelsMode}
        columnWidths={columnWidths}
        disableSticky={disableSticky}
        design={tableDesign}
        highlightCell={highlightCell}
        markedCells={markedCells}
      />
    )

    return fullscreen ? (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          zIndex: 1300,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          { controls }
          </Box>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          { table }
        </Box>
        { metadataModal }
      </Box>
    ) : (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}>
        { controls }
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          { table }
        </Box>
        { metadataModal }
      </Box>
    )
  } else if (viewMode === 'graph') {

    const controls = currentViewSettings && !hideControls && (
      <GraphViewControls
        allRows={graphMetadata.allRows}
        allColumns={graphMetadata.allColumns}
        allRowTypes={graphMetadata.allRowTypes}
        allColumnTypes={graphMetadata.allColumnTypes}
        allowedChartTypes={graphMetadata.allowedChartTypes}
        // The Data Type dropdown must SHOW the same value the chart plots —
        // graphConfig.dataType is the resolved one (Column % on an untouched
        // table, #1173 fault 4), while currentViewSettings still carries the
        // raw shared default 'freq' the table half owns. Spreading the
        // resolved value in also means any chart-view edit persists it
        // explicitly, which matches what the user was looking at.
        viewSettings={{ ...currentViewSettings, data_type: graphConfig.dataType }}
        onViewSettingsChange={handleViewSettingsChange}
        fullscreen={fullscreen}
        onFullscreenToggle={handleFullscreenToggle}
        onTableView={handleViewModeToggle}
        onInfoClick={handleInfoClick}
        onExportPptx={handleExportPptx}
      />
    )

    const graphs = (
      <PlatinumGraphs
        data={effectiveData}
        config={graphConfig}
        showControls={false}
        isLoading={false}
        allRowTypes={graphMetadata.allRowTypes}
        allColumnTypes={graphMetadata.allColumnTypes}
        colorMap={colorMap}
        onLegendToggle={onLegendToggle}
        design={design}
        respectParentHeight={respectParentHeight}
      />
    )
    return fullscreen ? (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          zIndex: 1300,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          { controls }
          </Box>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          { graphs }
        </Box>
        { metadataModal }
      </Box>
    ) : (
      <>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}>
          { controls }
            <Box sx={{
            flexGrow: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}>
            { graphs }
          </Box>
        </Box>
        { metadataModal }
      </>
    )
  }
})

PlatinumTableWrapper.displayName = 'PlatinumTableWrapper'

export default PlatinumTableWrapper
